import { isApiRequestError } from "./apiRequestError";
import {
  CANDLE_BAR_BUDGET_EXCEEDED_CODE,
  type SimulateCandleBudgetSuggestedPeriod,
} from "../types/tradePolicy";

export type CandleBarBudgetExceededInfo = {
  code: typeof CANDLE_BAR_BUDGET_EXCEEDED_CODE;
  message: string;
  maxBars?: number;
  analysisBarCount?: number;
  candleTimeframe?: string;
  suggestedPeriods?: SimulateCandleBudgetSuggestedPeriod[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readSuggestedPeriods(raw: unknown): SimulateCandleBudgetSuggestedPeriod[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: SimulateCandleBudgetSuggestedPeriod[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) continue;
    const window_start = typeof row.window_start === "string" ? row.window_start : "";
    const window_end = typeof row.window_end === "string" ? row.window_end : "";
    if (!window_start || !window_end) continue;
    out.push({
      window_start,
      window_end,
      ...(typeof row.bar_count === "number" ? { bar_count: row.bar_count } : {}),
      ...(typeof row.trade_count === "number" ? { trade_count: row.trade_count } : {}),
      ...(typeof row.is_current === "boolean" ? { is_current: row.is_current } : {}),
      ...(typeof row.label === "string" ? { label: row.label } : {}),
    });
  }
  return out.length > 0 ? out : undefined;
}

function extractFromPayload(payload: unknown): CandleBarBudgetExceededInfo | null {
  const root = asRecord(payload);
  if (!root) return null;

  const detail = root.detail;
  const detailObj = asRecord(detail);
  const details = asRecord(root.details) ?? detailObj;

  const codeCandidates = [
    typeof root.error === "string" ? root.error : null,
    typeof root.code === "string" ? root.code : null,
    typeof details?.code === "string" ? details.code : null,
    typeof detailObj?.code === "string" ? detailObj.code : null,
    typeof detail === "string" && detail.includes(CANDLE_BAR_BUDGET_EXCEEDED_CODE)
      ? CANDLE_BAR_BUDGET_EXCEEDED_CODE
      : null,
  ].filter(Boolean) as string[];

  if (!codeCandidates.some((c) => c === CANDLE_BAR_BUDGET_EXCEEDED_CODE)) {
    return null;
  }

  const message =
    (typeof root.message === "string" && root.message.trim()) ||
    (typeof detailObj?.message === "string" && detailObj.message.trim()) ||
    (typeof detail === "string" && detail.trim()) ||
    "Selected window exceeds the candle bar budget. Pick a shorter period.";

  const maxBars =
    (typeof details?.max_bars === "number" ? details.max_bars : undefined) ??
    (typeof detailObj?.max_bars === "number" ? detailObj.max_bars : undefined);
  const analysisBarCount =
    (typeof details?.analysis_bar_count === "number" ? details.analysis_bar_count : undefined) ??
    (typeof detailObj?.analysis_bar_count === "number" ? detailObj.analysis_bar_count : undefined);
  const candleTimeframe =
    (typeof details?.candle_timeframe === "string" ? details.candle_timeframe : undefined) ??
    (typeof detailObj?.candle_timeframe === "string" ? detailObj.candle_timeframe : undefined);
  const suggestedPeriods =
    readSuggestedPeriods(details?.suggested_periods) ??
    readSuggestedPeriods(detailObj?.suggested_periods);

  return {
    code: CANDLE_BAR_BUDGET_EXCEEDED_CODE,
    message,
    ...(maxBars != null ? { maxBars } : {}),
    ...(analysisBarCount != null ? { analysisBarCount } : {}),
    ...(candleTimeframe ? { candleTimeframe } : {}),
    ...(suggestedPeriods ? { suggestedPeriods } : {}),
  };
}

/** True when simulate failed because the window × candle TF exceeds the OHLC bar cap. */
export function isCandleBarBudgetExceededError(err: unknown): err is Error {
  return getCandleBarBudgetExceededInfo(err) != null;
}

export function getCandleBarBudgetExceededInfo(err: unknown): CandleBarBudgetExceededInfo | null {
  if (isApiRequestError(err)) {
    if (err.slug === CANDLE_BAR_BUDGET_EXCEEDED_CODE) {
      const fromDetails = extractFromPayload({
        error: err.slug,
        message: err.message,
        details: err.details,
      });
      if (fromDetails) return fromDetails;
      return {
        code: CANDLE_BAR_BUDGET_EXCEEDED_CODE,
        message: err.message,
        ...(typeof err.details?.max_bars === "number" ? { maxBars: err.details.max_bars } : {}),
        ...(typeof err.details?.analysis_bar_count === "number"
          ? { analysisBarCount: err.details.analysis_bar_count }
          : {}),
        ...(typeof err.details?.candle_timeframe === "string"
          ? { candleTimeframe: err.details.candle_timeframe }
          : {}),
        ...(readSuggestedPeriods(err.details?.suggested_periods)
          ? { suggestedPeriods: readSuggestedPeriods(err.details?.suggested_periods) }
          : {}),
      };
    }
    const fromRaw = extractFromPayload(err.details) ?? extractFromPayload({ detail: err.details });
    if (fromRaw) return fromRaw;
  }

  if (err instanceof Error) {
    if (err.message.includes(CANDLE_BAR_BUDGET_EXCEEDED_CODE)) {
      return {
        code: CANDLE_BAR_BUDGET_EXCEEDED_CODE,
        message: err.message,
      };
    }
  }

  return extractFromPayload(err);
}
